import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback, useRef } from "react";
import Dashboard from "@/pages/Dashboard";
import CasoDetalle from "@/pages/CasoDetalle";
import NuevoCaso from "@/pages/NuevoCaso";
import AdminUsuarios from "@/pages/AdminUsuarios";
import DataLake from "@/pages/DataLake";
import SystemLogs from "@/pages/SystemLogs";
import Aprobaciones from "@/pages/Aprobaciones";
import EditorFases from "@/pages/EditorFases";
import Papelera from "@/pages/Papelera";
import Abogados from "@/pages/Abogados";
import Login from "@/pages/Login";
import OAuthAutorizar from "@/pages/OAuthAutorizar";
import CorreosVault from "@/pages/CorreosVault";
import { Toaster } from "@/components/ui/sonner";
import axios from "axios";

const API_URL = process.env.REACT_APP_BACKEND_URL;
export const api = axios.create({
  baseURL: `${API_URL}/api`,
  withCredentials: true
});

// Interceptor: redirigir a login si 401
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && !window.location.pathname.includes("/login") && !window.location.pathname.includes("/oauth/autorizar")) {
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function AuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionId = new URLSearchParams(hash.replace("#", "?")).get("session_id");

    if (!sessionId) {
      navigate("/login", { replace: true });
      return;
    }

    const exchangeSession = async () => {
      try {
        const res = await api.post("/auth/session", { session_id: sessionId });
        if (res.data.success) {
          const pendTx = localStorage.getItem("oauth_tx");
          if (pendTx) {
            localStorage.removeItem("oauth_tx");
            navigate(`/oauth/autorizar?tx=${encodeURIComponent(pendTx)}`, { replace: true });
          } else {
            navigate("/dashboard", { replace: true, state: { user: res.data.user } });
          }
        } else {
          navigate("/login", { replace: true });
        }
      } catch {
        navigate("/login", { replace: true });
      }
    };
    exchangeSession();
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 mx-auto mb-3" style={{ borderColor: "var(--primary)" }} />
        <p className="text-sm" style={{ color: "var(--text-muted)" }}>Autenticando...</p>
      </div>
    </div>
  );
}

function AppRouter() {
  const location = useLocation();

  // Check for session_id in hash SYNCHRONOUSLY during render
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return <AppRoutes />;
}

function AppRoutes() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth, skip /me check
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If user data passed from AuthCallback
    if (location.state?.user) {
      setUser(location.state.user);
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth, location.state]);

  const handleLogout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setUser(null);
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg)" }}>
      <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: "var(--primary)" }} />
    </div>
  );

  return (
    <Routes>
      <Route path="/login" element={user ? <Navigate to="/dashboard" /> : <Login />} />
      <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Navigate to="/login" />} />
      <Route path="/dashboard" element={user ? <Dashboard user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/casos/nuevo" element={user ? <NuevoCaso user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/casos/:id" element={user ? <CasoDetalle user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/admin/usuarios" element={user ? <AdminUsuarios user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/admin/fases" element={user ? <EditorFases user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/papelera" element={user ? <Papelera user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/abogados" element={user ? <Abogados user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/data-lake" element={user ? <DataLake user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/correos-vault" element={user ? <CorreosVault user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/admin/logs" element={user ? <SystemLogs user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/admin/aprobaciones" element={user ? <Aprobaciones user={user} onLogout={handleLogout} /> : <Navigate to="/login" />} />
      <Route path="/oauth/autorizar" element={<OAuthAutorizar />} />
      <Route path="*" element={<Navigate to="/" />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="min-h-screen" style={{ background: "var(--bg)" }}>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
